<template>
  <Teleport to="body">
    <div
      class="toast-bottom-container"
      @mouseenter="handleMouseEnter"
      @mouseleave="handleMouseLeave"
    >
      <BaseClosableToast :toasts="displayToasts" width="340px" @remove="handleRemove" />
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { useToastStore } from '@template/stores';
import { BaseClosableToast } from '@template/ui';
import type { ToastItem } from '@template/ui';
import { watch, ref, onUnmounted } from 'vue';

const toastStore = useToastStore();
const toastTimers = new Map<string, NodeJS.Timeout>();
const toastShownAt = new Map<string, number>();
const isHovered = ref(false);
// store의 BottomToastItem을 그대로 복사해서 사용 (이미 variant 포함)
const displayToasts = ref<ToastItem[]>([]);
const incomingBottomToastSignal = ref(0);
const hasPendingQueuedIncomingToast = ref(false);
const estimatedBottomToastDepth = ref(0);
const DEFAULT_EXPOSURE_MS = 3000;
const REPLACEMENT_EXPOSURE_MS = 100;
const BOTTOM_TOAST_ADD_ACTIONS = new Set([
  'addBottomToast',
  'addOrderCompleteToast',
  'addExecutionCompleteToast',
  'addOrderTriggeredToast',
  'addOrderRejectedToast',
  'addOrderModifiedToast',
  'addOrderCancelledToast',
]);

/**
 * 토스트 자동 제거 타이머를 설정합니다.
 * 기존 타이머가 있으면 교체합니다.
 * @param id - 토스트 ID
 * @param delayMs - 제거까지 대기 시간(ms)
 */
const scheduleToastRemoval = (id: string, delayMs: number): void => {
  const prevTimer = toastTimers.get(id);
  if (prevTimer) {
    clearTimeout(prevTimer);
  }

  const timer = setTimeout(() => {
    handleRemove(id);
  }, delayMs);

  toastTimers.set(id, timer);
};

/**
 * 표시 중인 토스트 중 가장 오래된 토스트의 제거를 가속 시간(현재 100ms)으로 앞당깁니다.
 * @param toasts - 현재 화면에 표시 중인 토스트 목록
 */
const accelerateOldestVisibleToast = (toasts: Array<{ id: string; timestamp: number }>): void => {
  if (toasts.length === 0) {
    return;
  }

  const oldestToast = [...toasts].sort((a, b) => a.timestamp - b.timestamp)[0];
  const shownAt = toastShownAt.get(oldestToast.id) ?? Date.now();
  const elapsedMs = Date.now() - shownAt;
  const remainingMs = Math.max(DEFAULT_EXPOSURE_MS - elapsedMs, 0);

  if (remainingMs <= 0) {
    handleRemove(oldestToast.id);
    return;
  }

  scheduleToastRemoval(oldestToast.id, Math.min(remainingMs, REPLACEMENT_EXPOSURE_MS));
};

const stopActionListener = toastStore.$onAction(({ name }) => {
  if (BOTTOM_TOAST_ADD_ACTIONS.has(name)) {
    incomingBottomToastSignal.value += 1;
    estimatedBottomToastDepth.value += 1;
    if (isHovered.value && displayToasts.value.length >= 3) {
      hasPendingQueuedIncomingToast.value = true;
    }
  }

  if (name === 'removeBottomToast') {
    estimatedBottomToastDepth.value = Math.max(estimatedBottomToastDepth.value - 1, 0);
  }

  if (name === 'clearAllBottomToasts') {
    estimatedBottomToastDepth.value = 0;
  }
});

onUnmounted(() => {
  stopActionListener();
});

/**
 * 토스트 제거 핸들러
 */
const handleRemove = (id: string) => {
  // UI 목록에서도 제거
  displayToasts.value = displayToasts.value.filter((toast) => toast.id !== id);

  // 타이머 정리
  const timer = toastTimers.get(id);
  if (timer) {
    clearTimeout(timer);
    toastTimers.delete(id);
  }
  toastShownAt.delete(id);

  // store에서 제거
  toastStore.removeBottomToast(id);
};

const handleMouseEnter = () => {
  isHovered.value = true;
  // 모든 타이머 일시 정지
  toastTimers.forEach((timer) => clearTimeout(timer));
  toastTimers.clear();
};

const handleMouseLeave = () => {
  isHovered.value = false;

  // 현재 store 상태 기준으로 UI와 타이머 재설정
  displayToasts.value = toastStore.activeBottomToast;

  toastStore.activeBottomToast.forEach((toast) => {
    if (!toastShownAt.has(toast.id)) {
      toastShownAt.set(toast.id, Date.now());
    }
    scheduleToastRemoval(toast.id, DEFAULT_EXPOSURE_MS);
  });

  if (hasPendingQueuedIncomingToast.value) {
    accelerateOldestVisibleToast(toastStore.activeBottomToast);
    hasPendingQueuedIncomingToast.value = false;
  }
};

/**
 * store의 activeBottomToast 변경 감지 - 새로 추가된 토스트에 자동 제거 타이머 설정
 */
watch(
  () => [toastStore.activeBottomToast, incomingBottomToastSignal.value] as const,
  ([newToasts, incomingSignal], previousValue) => {
    const previousIncomingSignal = previousValue?.[1] ?? incomingSignal;
    const hasIncomingToast = incomingSignal !== previousIncomingSignal;

    // 호버 중에는 순서/타이머를 변경하지 않음
    if (isHovered.value) {
      return;
    }

    const previousToastIds = new Set(displayToasts.value.map((toast) => toast.id));
    const hasIncomingVisibleToast = newToasts.some((toast) => !previousToastIds.has(toast.id));

    // 화면에 표시할 리스트 동기화
    displayToasts.value = newToasts;

    // 모든 현재 토스트 ID
    const currentIds = new Set(newToasts.map((t) => t.id));

    // 새로 추가된 토스트는 기본 노출 시간(3초)으로 표시
    newToasts.forEach((toast) => {
      if (!toastShownAt.has(toast.id)) {
        toastShownAt.set(toast.id, Date.now());
      }

      if (!previousToastIds.has(toast.id)) {
        scheduleToastRemoval(toast.id, DEFAULT_EXPOSURE_MS);
      }
    });

    // 3개가 이미 표시 중일 때 대기열이 생기는 경우에만 교체 가속 적용
    const hasQueuedIncomingToast =
      hasIncomingToast && !hasIncomingVisibleToast && previousToastIds.size >= 3;
    const hasQueuedBacklog = estimatedBottomToastDepth.value > newToasts.length;

    // 대기열이 남아있는 동안에는(새 유입이 멈춰도) 가장 오래된 표시 메시지를 빠르게 교체
    if (hasQueuedIncomingToast || hasPendingQueuedIncomingToast.value || hasQueuedBacklog) {
      accelerateOldestVisibleToast(newToasts);
      hasPendingQueuedIncomingToast.value = false;
    }

    // 제거된 토스트의 타이머 정리
    toastTimers.forEach((timer, id) => {
      if (!currentIds.has(id)) {
        clearTimeout(timer);
        toastTimers.delete(id);
        toastShownAt.delete(id);
      }
    });
  },
  { deep: true, immediate: true }
);
</script>

<style scoped>
.toast-bottom-container {
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: var(--z-index-toast);
  /* 토스트 영역 위에 마우스가 올라갔을 때 hover 제어를 위해 이벤트 허용 */
  pointer-events: auto;
}
</style>
